import CreateWorkOrderFooter from '@/features/work-orders/create-work-order/components/CreateWorkOrderFooter.tsx';
import CreateWorkOrderRequiredFieldsAside from '@/features/work-orders/create-work-order/components/CreateWorkOrderRequiredFieldsAside.tsx';
import CreateWorkOrderTable from '@/features/work-orders/create-work-order/components/CreateWorkOrderTable.tsx';
import { Alert, AppShell } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedRowIdAtom } from '@/features/work-orders/create-work-order/store.ts';
import useCreateWorkOrderForm from '@/features/work-orders/create-work-order/hooks/useCreateWorkOrderForm.tsx';
import { FormProvider, useWatch } from 'react-hook-form';
import CreateWorkOrderHeader from '@/features/work-orders/create-work-order/components/CreateWorkOrderHeader.tsx';
import { useEffect } from 'react';
import {
  workOrderBatchRowsPaginationAtom,
  workOrderBatchRowsStatusAtom,
} from '@/features/work-orders/create-work-order/batchRowsState.ts';
import { DEFAULT_PAGINATION } from '@/common/pagination/types.ts';

const searchSchema = z.object({
  projectId: z.coerce.string().optional(),
});

export const Route = createFileRoute('/work-orders/create/$batchId')({
  component: RouteComponent,
  staticData: {
    authorization: {
      permission: {
        workOrder: ['write'],
      },
    },
  },
  validateSearch: searchSchema,
});

function RouteComponent() {
  const { batchId } = Route.useParams();
  const { projectId: initialProjectId } = Route.useSearch();

  return (
    <CreateWorkOrderBatchPage
      key={batchId}
      batchId={batchId}
      initialProjectId={initialProjectId}
    />
  );
}

function CreateWorkOrderBatchPage({
  batchId,
  initialProjectId,
}: {
  batchId: string;
  initialProjectId?: string;
}) {
  const setPagination = useSetAtom(workOrderBatchRowsPaginationAtom);
  const setStatus = useSetAtom(workOrderBatchRowsStatusAtom);
  const { form, summaryQuery, rowsQuery } = useCreateWorkOrderForm({
    batchId,
    initialProjectId,
  });
  const error = summaryQuery.error ?? rowsQuery.error;
  const isError = summaryQuery.isError || rowsQuery.isError;

  useEffect(() => {
    setPagination(DEFAULT_PAGINATION);
    setStatus(undefined);
  }, [batchId, setPagination, setStatus]);

  const selectedProjectId = useWatch({
    control: form.control,
    name: 'projectId',
  });
  const selectedRowId = useAtomValue(selectedRowIdAtom);

  return (
    <FormProvider {...form}>
      <AppShell
        header={{ height: 48 }}
        footer={{ height: 48 }}
        aside={selectedRowId ? { width: 360, breakpoint: 'md' } : undefined}
        padding={0}
      >
        <AppShell.Header
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
            backgroundColor: 'var(--mantine-color-default)',
          }}
        >
          <CreateWorkOrderHeader
            batchId={batchId}
            projectId={selectedProjectId}
            summary={summaryQuery.data}
          />
        </AppShell.Header>
        {selectedRowId && (
          <AppShell.Aside withBorder>
            <CreateWorkOrderRequiredFieldsAside />
          </AppShell.Aside>
        )}
        <AppShell.Main>
          {isError && (
            <Alert
              color="red"
              variant="light"
              title="Unable to load batch"
              m="sm"
            >
              {error instanceof Error ? error.message : String(error)}
            </Alert>
          )}
          {!isError && !selectedProjectId && (
            <Alert
              color="blue"
              variant="light"
              title="Start by choosing a project"
              m="sm"
            >
              The work order table will appear after you select a project.
            </Alert>
          )}

          {!isError && selectedProjectId && (
            <CreateWorkOrderTable
              batchId={batchId}
              rowsData={rowsQuery.data}
              summary={summaryQuery.data}
              loading={rowsQuery.isFetching && !!rowsQuery.data}
            />
          )}
        </AppShell.Main>
        <AppShell.Footer>
          <CreateWorkOrderFooter batchId={batchId} rowsData={rowsQuery.data} />
        </AppShell.Footer>
      </AppShell>
    </FormProvider>
  );
}
